<script setup>
import {ref, computed, watch, onMounted} from 'vue';
import {useRouter, useRoute} from 'vue-router';
import {useAuthService} from '~/composables/useAuthService';
import SocialLoginButtons from "~/components/auth/SocialLoginButtons.client.vue";
import SocialAccountNotFound from "~/components/auth/SocialAccountNotFound.vue";
import {useHttpRequest} from '~/composables/useHttpRequest';
import {endpoints} from '~/network/endpoints.js';
import {getPasskey, passkeyCancelled, passkeyProblem, passkeysSupported} from '~/utils/passkey';

const router = useRouter();
const route = useRoute();

const {base64StringToObj} = useCrudConverters();
// Props
const props = defineProps({
  redirectUrl: {
    type: String,
    default: "/",
  },
  authId: {
    type: String,
    default: null,
  },
  // hides the hero block when the form is embedded in another card (invite page)
  hideHero: {
    type: Boolean,
    default: false,
  },
  // email only — used inside the "sign in and link" step
  hideSocial: {
    type: Boolean,
    default: false,
  },
  // After a successful email sign-in, attach this pending social identity
  // (ACCOUNT_IDENTITY_PLAN.md layer 2) before moving on.
  linkPendingToken: {
    type: String,
    default: null,
  },
});
// A social sign-in nobody uses: the buttons hand us the pending identity and we
// show the link-or-create question instead of the form.
const accountNotFound = ref(null);

// Data properties
const email = ref(null);
const password = ref(null);
const showPassword = ref(false);

const {
  login,
  loginLoading,
  loginErrors,
  hasLoginErrors,
  externalLoginErrors,
  externalLoginLink,
  externalLoginLinkErrors,
  loginVerification,
  loginVerificationLoading,
  loginVerificationErrors,
  loginVerificationItem,
  hasLoginVerificationErrors
} = useAuthService();


const passwordRef = ref(false);
const emailRef = ref(false);

const isLoading = computed(() => {
  return loginLoading.value || loginVerificationLoading.value;
});
const isVerifiedEmail = computed(() => {
  return loginVerificationItem.value && loginVerificationItem.value.email && !loginVerificationItem.value.error;
});
const canEnterPassword = computed(() => {
  return isVerifiedEmail.value;
});

const isValidEmail = (email) => {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}
const isButtonDisabled = computed(() => {
  if (!canEnterPassword.value) {
    return !isValidEmail(email.value);
  } else {
    return !password.value || password.value.length < 1
  }
});

// Set when the sign-in worked but attaching the social identity did not. The person
// is already signed in at that point, so the screen must not become a dead end.
const linkFailed = ref(false);

// Sign in with a passkey: no email, no password, no code. The device offers
// the passkey it holds for this site, and the passkey names the account.
const {httpRequest} = useHttpRequest();
const {adoptSession} = useAuthService();
const canPasskey = ref(false);
onMounted(() => { canPasskey.value = passkeysSupported(); });
const passkeyBusy = ref(false);
const passkeyError = ref('');
const signInWithPasskey = async () => {
  passkeyBusy.value = true;
  passkeyError.value = '';
  try {
    const ask = await httpRequest(endpoints.passkeySignIn.options, {});
    const assertionJson = await getPasskey(ask.optionsJson);
    const result = await httpRequest(endpoints.passkeySignIn.login, {assertionJson, pendingToken: ask.pendingToken});
    adoptSession(result);
    await goOn();
  } catch (e) {
    if (!passkeyCancelled(e)) passkeyError.value = passkeyProblem(e) ?? e?.global?.[0] ?? e?.message ?? 'That passkey was not accepted. Sign in another way.';
  } finally {
    passkeyBusy.value = false;
  }
};

const goOn = async () => {
  if (props.redirectUrl) {
    // string form keeps the return-to's query; {path} would drop it
    await router.push(props.redirectUrl);
  }
};

const formAction = async () => {
  linkFailed.value = false; // a retry starts clean, or the way-out button outlives its reason
  if (isVerifiedEmail.value) {
    const loginData = {email: email.value, password: password.value};
    if (props.authId) {
      loginData.authId = props.authId;
    }
    await login(loginData);
    if (hasLoginErrors.value) return;
    if (props.linkPendingToken) {
      await externalLoginLink({pendingIdentityToken: props.linkPendingToken});
      if (externalLoginLinkErrors.value && Object.keys(externalLoginLinkErrors.value).length) {
        // Linking failed, but login() above already succeeded and stored the token,
        // so this person IS signed in. Stopping here is right (they should learn the
        // link did not happen) but stopping SILENTLY is what turned a server error
        // into a frozen screen. Show the reason and offer the way on.
        linkFailed.value = true;
        return;
      }
      const {setAlertMessage} = useAlertService();
      setAlertMessage({message: 'Linked. Next time, one tap signs you in', color: 'success'});
    }
    await goOn();
  } else {
    await loginVerification({email: email.value});
    if (isVerifiedEmail.value) {
      passwordRef.value?.focus();
    }
  }
};
const verificationErrors = computed(() => {
  if(loginVerificationItem.value && loginVerificationItem.value.error) {
    return {
      global: [loginVerificationItem.value.error]
    }
  }
  return {};
});
const changeVerifiedEmail = () => {
  email.value = null;
  loginVerificationItem.value = null;
  password.value = null;
  emailRef.value?.focus();
}

</script>

<template>
  <social-account-not-found
      v-if="accountNotFound"
      :pending="accountNotFound" :redirect-url="redirectUrl" :auth-id="authId"
      @back="accountNotFound = null"
  />
  <div v-else>
    <!-- Two-step sign-in (IMPLEMENTATION-LOG blocker #1): step 1 collects the email and
         checks it via the EXISTING api/v2/auth/login/verification call; step 2 locks the
         email and asks for the password. No new endpoints were added. -->
    <div v-if="!hideHero" class="auth-hero mb-2">
      <div class="auth-logo"><img src="/images/icon_red.png" alt="Tap Leagues"/></div>
      <h1>{{ isVerifiedEmail ? 'Enter your password' : 'Welcome back' }}</h1>
      <p v-if="isVerifiedEmail">Your email checked out. Finish signing in below.</p>
      <p v-else>Sign in to see your matches, record scores and follow your standings.</p>
    </div>

    <template v-if="!hideSocial">
      <social-login-buttons :redirect-url="redirectUrl" :auth-id="authId" @account-not-found="accountNotFound = $event"/>
      <crud-error-display class="mt-3" :errors="externalLoginErrors" v-if="externalLoginErrors"/>
      <v-btn v-if="canPasskey && !linkPendingToken" :block="true" size="large" variant="outlined" class="auth-social-btn mt-2"
             :loading="passkeyBusy" @click="signInWithPasskey">
        <v-icon size="18" icon="mdi-fingerprint" class="mr-2"></v-icon>
        Sign in with a passkey
      </v-btn>
      <div v-if="passkeyError" class="auth-passkey-error">{{ passkeyError }}</div>
      <div class="auth-divider">or sign in with email</div>
    </template>

    <v-form @submit.prevent="formAction">
      <!-- Step 2: email verified and locked -->
      <template v-if="isVerifiedEmail">
        <div class="tl-label mb-1">Email address</div>
        <div class="auth-locked-row">
          <v-icon size="18" color="success" icon="mdi-check-circle"/>
          <span class="email">{{ loginVerificationItem.email }}</span>
          <a class="change" @click="changeVerifiedEmail">Change</a>
        </div>
        <div class="auth-help mb-4">Enter your password to sign in.</div>

        <div class="tl-label mb-1">Password</div>
        <v-text-field
            ref="passwordRef"
            autocomplete="false"
            density="compact"
            :loading="isLoading"
            :disabled="isLoading"
            :type="showPassword ? 'text' : 'password'"
            v-model="password"
            placeholder="••••••••"
            prepend-inner-icon="mdi-lock-outline"
            :append-inner-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
            @click:append-inner="showPassword = !showPassword"
            hide-details="auto"
        ></v-text-field>
        <div class="auth-help">
          <router-link :to="{ name: 'ForgotPassword' }">Forgot your password?</router-link>
        </div>
      </template>

      <!-- Step 1: email entry -->
      <template v-else>
        <div class="tl-label mb-1">Email address</div>
        <v-text-field
            ref="emailRef"
            :clearable="true"
            autocomplete="false"
            density="compact"
            :loading="isLoading"
            :disabled="isLoading"
            v-model="email"
            type="email"
            placeholder="you@club.com"
            prepend-inner-icon="mdi-email-outline"
            :error="loginErrors.email != null"
            :error-messages="loginErrors.email"
            hide-details="auto"
        ></v-text-field>
        <div class="auth-help">We'll check your email first, then your password.</div>
        <div class="auth-help">
          <router-link :to="{ name: 'ForgotPassword' }">Forgot your password?</router-link>
        </div>
        <crud-error-display class="mt-3" :errors="verificationErrors"/>
      </template>

      <crud-error-display class="mt-3" :errors="loginErrors"/>
      <!-- show-all matters: without it this renders errors.global only, so a
           field-scoped 400 shows nothing at all and the screen simply stops. -->
      <crud-error-display class="mt-3" :show-all="true" :errors="externalLoginLinkErrors"
                          v-if="linkPendingToken && externalLoginLinkErrors"/>

      <v-btn :disabled="isButtonDisabled" type="submit" size="large" :block="true" class="mt-5"
             color="primary" :loading="isLoading">
        {{ isVerifiedEmail ? (linkPendingToken ? 'Sign in and link' : 'Sign in') : 'Continue' }}
      </v-btn>

      <v-btn v-if="linkFailed" type="button" variant="text" size="large" :block="true" class="mt-2"
             color="primary" @click="goOn">
        Continue without linking
      </v-btn>
      <div v-if="linkFailed" class="auth-help mt-1">
        You're signed in. You can link your account later from your profile.
      </div>
    </v-form>

    <div v-if="!authId" class="auth-foot">
      New to Tap Leagues? <router-link :to="{path: '/register', query: $route.query}">Create an account</router-link>
    </div>
  </div>
</template>

<style scoped>

.auth-passkey-error { font-size: 12.5px; color: rgb(var(--v-theme-error)); margin-top: 8px; text-align: center; }
</style>
